import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { blendApi, Booster } from "./api";
import { parseConcentration } from "./ui";

type Recipe = {
  name: string;
  base_target_id?: string;
  blend_id?: string;
  warnings?: string[];
  body: { oil: string; pct: number };
  direction: { oil: string; pct: number };
  boosters: Booster[];
  concentration: string | null;
  batch?: {
    bottles: number; sizeMl: number; conc: number; currency: string;
    priceGOil: number; priceMlAlc: number; packaging: number; sellPrice: number;
  } | null;
};

const EMPTY: Recipe = {
  name: "وصفة يدوية",
  body: { oil: "Body Oil", pct: 58 },
  direction: { oil: "Direction Oil", pct: 32 },
  boosters: [],
  concentration: "20%",
};

const r2 = (n: number) => Math.round(n * 100) / 100;

function Num({ label, value, onChange, step = 1, testId }: {
  label: string; value: number; onChange: (v: number) => void; step?: number; testId: string;
}) {
  return (
    <div>
      <label className="bl-label">{label}</label>
      <input type="number" className="bl-input" value={value} step={step} min={0}
        onChange={(e) => onChange(Number(e.target.value) || 0)} data-testid={testId} />
    </div>
  );
}

export default function CalculatorPage() {
  const [recipe, setRecipe] = useState<Recipe>(EMPTY);
  const [handoff, setHandoff] = useState(false);
  const [bottles, setBottles] = useState(10);
  const [sizeMl, setSizeMl] = useState(50);
  const [conc, setConc] = useState(20);
  const [oilDensity, setOilDensity] = useState(0.9);
  const [alcDensity, setAlcDensity] = useState(0.79);
  const [dropsPerMl, setDropsPerMl] = useState(20);
  const [bhtPct, setBhtPct] = useState(0.1);
  const [priceGOil, setPriceGOil] = useState(0);
  const [priceMlAlc, setPriceMlAlc] = useState(0);
  const [packaging, setPackaging] = useState(0);
  const [sellPrice, setSellPrice] = useState(0);
  const [currency, setCurrency] = useState("EGP");
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [blendId, setBlendId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [batchResult, setBatchResult] = useState<{ code: string; lowStock: any[] } | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("blend_handoff");
    if (!raw) return;
    try {
      const r = JSON.parse(raw) as Recipe;
      setRecipe(r);
      setConc(parseConcentration(r.concentration));
      if (r.batch) {
        setBottles(r.batch.bottles); setSizeMl(r.batch.sizeMl); setConc(r.batch.conc);
        setCurrency(r.batch.currency); setPriceGOil(r.batch.priceGOil);
        setPriceMlAlc(r.batch.priceMlAlc); setPackaging(r.batch.packaging); setSellPrice(r.batch.sellPrice);
      }
      setHandoff(true);
    } catch { /* ignore */ }
  }, []);

  const calc = useMemo(() => {
    const V = bottles * sizeMl;
    const concentrate = V * conc / 100;
    const bodyMl = concentrate * recipe.body.pct / 100;
    const directionMl = concentrate * recipe.direction.pct / 100;
    const boosterRows = recipe.boosters.map((b) => ({ ...b, ml: V * b.pct / 100 }));
    const boostersMl = boosterRows.reduce((s, b) => s + b.ml, 0);
    const bhtMl = V * bhtPct / 100;
    const alcoholMl = V - concentrate - boostersMl - bhtMl;
    const oilMl = bodyMl + directionMl + boostersMl + bhtMl;
    const oilG = oilMl * oilDensity;
    const alcoholG = alcoholMl * alcDensity;
    const effConc = V > 0 ? ((concentrate + boostersMl) / V) * 100 : 0;
    const materialsCost = oilG * priceGOil + Math.max(alcoholMl, 0) * priceMlAlc;
    const costPerBottle = bottles > 0 ? materialsCost / bottles + packaging : 0;
    const batchCost = materialsCost + packaging * bottles;
    const profit = sellPrice - costPerBottle;
    const margin = sellPrice > 0 ? (profit / sellPrice) * 100 : 0;
    return { V, concentrate, bodyMl, directionMl, boosterRows, boostersMl, bhtMl, alcoholMl, oilG, alcoholG, effConc, costPerBottle, batchCost, profit, margin };
  }, [bottles, sizeMl, conc, recipe, oilDensity, alcDensity, bhtPct, priceGOil, priceMlAlc, packaging, sellPrice]);

  const saveBlend = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const b = await blendApi.saveBlend({
        name: saveName.trim() || recipe.name,
        base_target_id: recipe.base_target_id || "",
        body: recipe.body, direction: recipe.direction,
        boosters: recipe.boosters, concentration: `${conc}%`,
        warnings: recipe.warnings || [],
        batch: {
          bottles, sizeMl, conc, effConc: r2(calc.effConc),
          costPerBottle: r2(calc.costPerBottle), batchCost: r2(calc.batchCost),
          profit: r2(calc.profit), margin: r2(calc.margin),
          currency, priceGOil, priceMlAlc, packaging, sellPrice,
        },
      });
      setBlendId((b as any).id);
      setSaved(true);
      toast.success(`تم الحفظ ✓ — ${(b as any).code || ""}`);
    } catch (e: any) {
      toast.error(String(e.message || e));
    } finally { setSaving(false); }
  };

  const confirmBatch = async () => {
    const id = blendId || recipe.blend_id;
    if (!id) { toast.error("احفظ التركيبة الأول"); return; }
    setConfirming(true);
    try {
      const components = [
        { material: recipe.body.oil, grams: r2(calc.bodyMl * oilDensity) },
        { material: recipe.direction.oil, grams: r2(calc.directionMl * oilDensity) },
        ...calc.boosterRows.map((b) => ({ material: b.material, grams: r2(b.ml * oilDensity) })),
      ];
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend/blends/${id}/batches`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bottles, sizeMl, marketId: currency === "EGP" ? "eg" : "sa", components,
          costSnapshot: { costPerBottle: r2(calc.costPerBottle), batchCost: r2(calc.batchCost), currency },
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || res.statusText);
      const j = await res.json();
      setBatchResult({ code: j.code, lowStock: j.lowStock || [] });
      toast.success(`تشغيلة ${j.code} — اتخصم المخزون ✓`);
    } catch (e: any) {
      toast.error(String(e.message || e));
    } finally { setConfirming(false); }
  };

  const row = (label: string, ml: number, density: number, en = true) => (
    <tr key={label}>
      <td className={en ? "bl-en" : ""}>{label}</td>
      <td>{r2(ml)}</td>
      <td>{r2(ml * density)}</td>
      <td>{Math.round(ml * dropsPerMl)}</td>
    </tr>
  );

  return (
    <div className="max-w-3xl space-y-4" data-testid="blend-calculator-page">
      <div className="bl-card">
        <div className="bl-eyebrow">الوصفة</div>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <span className="font-extrabold" data-testid="calc-recipe-name">{recipe.name}</span>
          {handoff && <span className="bl-pill" data-testid="calc-handoff-pill">وصلت من التوصية ✓</span>}
        </div>
        <div className="bl-sub bl-en mt-1">
          {recipe.body.oil} {recipe.body.pct}% + {recipe.direction.oil} {recipe.direction.pct}%
          {recipe.boosters.length > 0 && ` + ${recipe.boosters.length} boosters`}
        </div>
        <div className="grid gap-2 mt-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
          <Num label="عدد الزجاجات" value={bottles} onChange={setBottles} testId="calc-bottles-input" />
          <Num label="سعة الزجاجة (مل)" value={sizeMl} onChange={setSizeMl} testId="calc-size-input" />
          <Num label="التركيز %" value={conc} onChange={setConc} step={0.5} testId="calc-conc-input" />
          <Num label="BHT % من الحجم" value={bhtPct} onChange={setBhtPct} step={0.05} testId="calc-bht-input" />
        </div>
        <details className="mt-3">
          <summary className="bl-sub cursor-pointer" data-testid="calc-settings-toggle">إعدادات (كثافة/قطرات)</summary>
          <div className="grid gap-2 mt-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
            <Num label="كثافة الزيت جم/مل" value={oilDensity} onChange={setOilDensity} step={0.01} testId="calc-oil-density" />
            <Num label="كثافة الكحول جم/مل" value={alcDensity} onChange={setAlcDensity} step={0.01} testId="calc-alc-density" />
            <Num label="قطرات/مل" value={dropsPerMl} onChange={setDropsPerMl} testId="calc-drops-per-ml" />
          </div>
        </details>
      </div>

      <div className="bl-card">
        <div className="bl-eyebrow mb-2">جدول التشغيلة — {r2(calc.V)} مل إجمالي</div>
        <table className="bl-table" data-testid="calc-batch-table">
          <thead>
            <tr><th>المكوّن</th><th>مل</th><th>جرام</th><th>قطرات</th></tr>
          </thead>
          <tbody>
            {row(`${recipe.body.oil} (${recipe.body.pct}%)`, calc.bodyMl, oilDensity)}
            {row(`${recipe.direction.oil} (${recipe.direction.pct}%)`, calc.directionMl, oilDensity)}
            {calc.boosterRows.map((b) => row(`${b.material} (${b.pct}%)`, b.ml, oilDensity))}
            {bhtPct > 0 && row(`BHT (${bhtPct}%)`, calc.bhtMl, oilDensity)}
            {row("كحول (Perfumer's Alcohol)", calc.alcoholMl, alcDensity, false)}
            <tr className="total">
              <td>الإجمالي</td>
              <td>{r2(calc.V)}</td>
              <td>{r2(calc.oilG + calc.alcoholG)}</td>
              <td>{Math.round(calc.V * dropsPerMl)}</td>
            </tr>
          </tbody>
        </table>
        <div className="bl-sub mt-2" data-testid="calc-effective-conc">
          التركيز الفعلي بعد المُعزّزات: <span style={{ color: "var(--bl-gold)" }}>{r2(calc.effConc)}%</span>
        </div>
        {calc.alcoholMl < 0 && (
          <div className="mt-2 bl-pill danger" data-testid="calc-negative-alcohol-warning">
            ⚠️ النِسب أكبر من حجم التشغيلة — قلّل المُعزّزات أو التركيز
          </div>
        )}
      </div>

      <div className="bl-card">
        <div className="bl-eyebrow mb-2">التكلفة والهامش</div>
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))" }}>
          <Num label={`سعر جرام الزيت (${currency})`} value={priceGOil} onChange={setPriceGOil} step={0.1} testId="calc-price-oil" />
          <Num label={`سعر مل الكحول (${currency})`} value={priceMlAlc} onChange={setPriceMlAlc} step={0.01} testId="calc-price-alcohol" />
          <Num label={`تغليف/زجاجة (${currency})`} value={packaging} onChange={setPackaging} step={1} testId="calc-packaging" />
          <Num label={`سعر البيع/زجاجة (${currency})`} value={sellPrice} onChange={setSellPrice} step={1} testId="calc-sell-price" />
          <div>
            <label className="bl-label">العملة</label>
            <select className="bl-select" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="calc-currency">
              <option value="EGP">EGP</option>
              <option value="SAR">SAR</option>
            </select>
          </div>
        </div>
        <table className="bl-table mt-3" data-testid="calc-cost-table">
          <tbody>
            <tr><td>تكلفة الزجاجة الواحدة</td><td data-testid="calc-cost-per-bottle">{r2(calc.costPerBottle)} {currency}</td></tr>
            <tr><td>تكلفة التشغيلة كاملة</td><td data-testid="calc-batch-cost">{r2(calc.batchCost)} {currency}</td></tr>
            <tr><td>الربح/زجاجة</td><td data-testid="calc-profit" style={{ color: calc.profit >= 0 ? "var(--bl-ok)" : "var(--bl-danger)" }}>{r2(calc.profit)} {currency}</td></tr>
            <tr className="total"><td>الهامش</td><td data-testid="calc-margin">{r2(calc.margin)}%</td></tr>
          </tbody>
        </table>
      </div>

      <div className="bl-card" data-testid="calc-save-section">
        <div className="bl-eyebrow mb-2">💾 احفظ التركيبة</div>
        <div className="flex gap-2 flex-wrap items-end">
          <div style={{ flex: 1, minWidth: 200 }}>
            <label className="bl-label">اسم التركيبة</label>
            <input className="bl-input" value={saveName} placeholder={recipe.name}
              onChange={(e) => setSaveName(e.target.value)} data-testid="calc-save-name-input" />
          </div>
          <button className="bl-btn primary" onClick={saveBlend} disabled={saving} data-testid="calc-save-btn">
            {saving ? "بيحفظ…" : "💾 احفظ التركيبة"}
          </button>
          <button className="bl-btn ghost" onClick={confirmBatch} disabled={confirming || (!saved && !recipe.blend_id)}
            data-testid="calc-confirm-batch-btn">
            {confirming ? "بيأكّد…" : "🏭 أكّد التشغيلة واخصم المخزون"}
          </button>
        </div>
        {saved && <div className="bl-pill mt-2" style={{ color: "var(--bl-ok)" }} data-testid="calc-saved-confirmation">تم الحفظ ✓ — هتلاقيها في «تركيباتي»</div>}
        {batchResult && (
          <div className="mt-2 space-y-1" data-testid="calc-batch-confirmed">
            <div className="bl-pill" style={{ color: "var(--bl-ok)" }}>تشغيلة {batchResult.code} — اتخصمت الخامات من المخزون ✓</div>
            {batchResult.lowStock.map((l, i) => (
              <div key={i} className="bl-pill danger" data-testid="calc-low-stock-alert">⚠️ {l.name} قرب يخلص: {l.stockOnHand} جم (حد الطلب {l.reorderPoint})</div>
            ))}
          </div>
        )}
      </div>

      <p className="bl-disclaimer" data-testid="calc-disclaimer">
        * التوصية نقطة بداية — تتأكد بالشمّ واختبار الثبات قبل الإنتاج.
      </p>
    </div>
  );
}
